import {useState} from "react";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {bookingAPI} from "../common/api/bookingAPI";
import {
    API_WAITING_LIST,
    API_WAITING_LIST_CAN_ENTER,
    API_WAITING_LIST_DECLINE,
    API_WAITING_LIST_UPSERT,
    API_WAITING_LIST_WITH_BOOKABLE,
} from "../common/api/apiConstants";
import {createURLQueryFromParams} from "../common/api/apiUtil";

export const useWaitingList = () => {

    const {initialized, authenticated} = useKeycloakFunctions();

    const [waitingList, setWaitingList] = useState();
    const [loadingGetWaitingList, setLoadingGetWaitingList] = useState(false);
    const [errorGetWaitingList, setErrorGetWaitingList] = useState(null);

    const [lastDeletedWaitingListEntryId, setLastDeletedWaitingListEntryId] = useState(null);
    const [deletingWaitingListLoading, setDeletingWaitingListLoading] = useState(false);
    const [deleteWaitingListError, setDeleteWaitingListError] = useState(null);

    const [newWaitingList, setNewWaitingList] = useState();
    const [createWaitingListLoading, setCreateWaitingListLoading] = useState(false);
    const [createWaitingListError, setCreateWaitingListError] = useState();

    const [waitingListWithBookable, setWaitingListWithBookable] = useState();
    const [loadingGetWaitingListWithBookable, setLoadingGetWaitingListWithBookable] = useState(false);
    const [errorGetWaitingListWithBookable, setErrorGetWaitingListWithBookable] = useState(null);

    const [declinedWaitingListOfferResponse, setDeclinedWaitingListOfferResponse] = useState(null);
    const [decliningWaitingListOfferLoading, setDecliningWaitingListOfferLoading] = useState(false);
    const [declineWaitingListOfferError, setDeclineWaitingListOfferError] = useState(null);

    const [canEnterWaitingList, setCanEnterWaitingList] = useState();
    const [loadingCanEnterWaitingList, setLoadingCanEnterWaitingList] = useState(false);
    const [errorCanEnterWaitingList, setErrorCanEnterWaitingList] = useState(null);

    const getWaitingList = () => {
        if (initialized && authenticated) {
            setLoadingGetWaitingList(true);
            setErrorGetWaitingList(null);

            return bookingAPI.get(API_WAITING_LIST, {redirectOnError: true})
                .then(res => {
                    setWaitingList(res.data);
                    return res.data;
                })
                .catch(err => {
                    setErrorGetWaitingList(err);
                    setWaitingList(null);
                    throw err;
                })
                .finally(() => {
                    setLoadingGetWaitingList(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const deleteWaitingList = (waitingListEntryId) => {
        if (initialized && authenticated) {
            setDeletingWaitingListLoading(true);
            setDeleteWaitingListError(null);

            return bookingAPI.delete(API_WAITING_LIST + "/" + waitingListEntryId)
                .then(res => {
                    setLastDeletedWaitingListEntryId(res?.data?.waiting_list_entry_id);
                })
                .catch(err => {
                    setDeleteWaitingListError(err.response);
                })
                .finally(() => {
                    setDeletingWaitingListLoading(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const createWaitingList = (resort_id, apartment_category, date_from, date_to, number_of_guests, number_of_nights = null, weekend = false) => {
        if (initialized && authenticated) {
            setNewWaitingList(null);
            setCreateWaitingListLoading(true);
            setCreateWaitingListError(null);

            return bookingAPI.post(API_WAITING_LIST + API_WAITING_LIST_UPSERT, {
                resort_id,
                apartment_category,
                date_from,
                date_to,
                number_of_guests,
                number_of_nights,
                weekend
            })
                .then(res => {
                    setNewWaitingList(res?.data);
                })
                .catch(err => {
                    setCreateWaitingListError(err);
                })
                .finally(() => {
                    setCreateWaitingListLoading(false);
                });
        }
    };

    const getWaitingListWithBookable = () => {
        if (initialized && authenticated) {
            setLoadingGetWaitingListWithBookable(true);
            setErrorGetWaitingListWithBookable(null);

            return bookingAPI.get(API_WAITING_LIST + API_WAITING_LIST_WITH_BOOKABLE, {redirectOnError: true})
                .then(res => {
                    setWaitingListWithBookable(res.data);
                    return res.data;
                })
                .catch(err => {
                    setErrorGetWaitingListWithBookable(err);
                    setWaitingListWithBookable({waiting_list: [], ready_to_book: []});
                    throw err;
                })
                .finally(() => {
                    setLoadingGetWaitingListWithBookable(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const declineWaitingListOffer = (resort_id, waitingListEntryId) => {
        if (initialized && authenticated) {
            setDecliningWaitingListOfferLoading(true);
            setDeclineWaitingListOfferError(null);

            const body = {
                resort_id: resort_id,
                reservation_id: waitingListEntryId
            };

            return bookingAPI.post(API_WAITING_LIST + API_WAITING_LIST_DECLINE, body)
                .then(res => {
                    setDeclinedWaitingListOfferResponse(res);
                })
                .catch(err => {
                    setDeclineWaitingListOfferError(err.response);
                })
                .finally(() => {
                    setDecliningWaitingListOfferLoading(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    const getCanEnterWaitingList = (resort_id, apartment_short_codes, date_from, date_to, number_of_guests = null, number_of_nights = null, weekend = null) => {
        if (initialized && authenticated) {
            setCanEnterWaitingList(null);
            setLoadingCanEnterWaitingList(true);
            setErrorCanEnterWaitingList(null);

            const params = {
                resort_id,
                apartment_short_codes,
                date_from,
                date_to,
            };

            if (number_of_guests) {
                params.number_of_guests = number_of_guests;
            }

            if (number_of_nights) {
                params.number_of_nights = number_of_nights;
            }

            if (weekend !== undefined && weekend !== null) {
                params.weekend = weekend;
            }

            return bookingAPI.get(API_WAITING_LIST + API_WAITING_LIST_CAN_ENTER + createURLQueryFromParams(params), {redirectOnError: false})
                .then(res => {
                    setCanEnterWaitingList(res.data);
                    return res.data;
                })
                .catch(err => {
                    setErrorCanEnterWaitingList(err);
                    setCanEnterWaitingList(null);
                    throw err;
                })
                .finally(() => {
                    setLoadingCanEnterWaitingList(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    return {
        getWaitingList,
        waitingList,
        loadingGetWaitingList,
        errorGetWaitingList,

        deleteWaitingList,
        lastDeletedWaitingListEntryId,
        deletingWaitingListLoading,
        deleteWaitingListError,

        createWaitingList,
        newWaitingList,
        createWaitingListError,
        createWaitingListLoading,

        getWaitingListWithBookable,
        waitingListWithBookable,
        loadingGetWaitingListWithBookable,
        errorGetWaitingListWithBookable,

        declineWaitingListOffer,
        declinedWaitingListOfferResponse,
        decliningWaitingListOfferLoading,
        declineWaitingListOfferError,

        getCanEnterWaitingList,
        canEnterWaitingList,
        loadingCanEnterWaitingList,
        errorCanEnterWaitingList
    };
};
